'use client';

import Script from 'next/script';
import { SITE_CONFIG } from '@/constants';
import { useCookieConsent } from '@/src/context/CookieConsentContext';

declare global {
  interface Window {
    fbq: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

function getPixelIds(): string[] {
  const raw = SITE_CONFIG.features.analytics.metaPixel.ids;
  if (!raw) return [];
  return raw.split(',').map((id) => id.trim()).filter(Boolean);
}

// False during SSR, before fbevents.js defines fbq, or with no pixel configured.
function canFireFbq(): boolean {
  return typeof window !== 'undefined'
    && typeof window.fbq === 'function'
    && getPixelIds().length > 0;
}

// No console.* in this string: compiler.removeConsole cannot strip inside
// string literals, so anything here logs on every prod page load (ACEB-222).
function buildInitScript(pixelIds: string[]): string {
  const initCalls = pixelIds.map((id) => `fbq('init', '${id}');`).join('\n');
  return `
    if (typeof fbq === 'function') {
      ${initCalls}
      fbq('track', 'PageView');
    }
  `;
}

export default function MetaPixel() {
  const { categories } = useCookieConsent();
  const pixelIds = getPixelIds();

  // fbevents.js (~110 KiB, third-party) only loads once marketing consent is
  // granted. The head fbq stub queues everything fired before that (init,
  // PageView, funnel events); fbevents drains the queue on load (ACEB-220).
  if (pixelIds.length === 0 || !categories.marketing) {
    return null;
  }

  return (
    <>
      <Script
        id="meta-pixel-js"
        src="https://connect.facebook.net/en_US/fbevents.js"
        strategy="afterInteractive"
        // fbevents stops draining the queue at the head stub's 'consent revoke'
        // and a grant QUEUED behind it never unblocks (verified on prod) - only
        // a LIVE post-load grant releases init/PageView. Marketing consent is
        // granted whenever this renders; if it's revoked while the script is
        // still loading, the component unmounts and onLoad never fires.
        onLoad={() => updateMetaConsent(true)}
      />
      <Script
        id="meta-pixel-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: buildInitScript(pixelIds) }}
      />
    </>
  );
}

export function updateMetaConsent(granted: boolean) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('consent', granted ? 'grant' : 'revoke');
}

export function trackMetaEvent(eventName: string, params?: Record<string, unknown>, eventId?: string) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  if (eventId) {
    window.fbq('track', eventName, params || {}, { eventID: eventId });
  } else {
    window.fbq('track', eventName, params || {});
  }
}

export function trackMetaPurchase(orderId: string, value: number, currency: string, contentIds?: string[]) {
  if (!canFireFbq()) return;

  const params = {
    value,
    currency,
    content_ids: contentIds ?? [orderId],
    content_type: 'product',
  };

  window.fbq('track', 'Purchase', params, { eventID: orderId });
}

type FunnelEventName = 'ViewContent' | 'AddToCart' | 'InitiateCheckout';

interface FunnelEventParams {
  value?: number;
  currency?: string;
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  num_items?: number;
}

function newEventId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

// Fires fbq browser-side and returns the eventId so the caller can pass it
// through whatever existing API mutation accompanies this event (cart add,
// order create, etc.). The server fires CAPI with the same eventId and Meta
// dedupes. Returns null when the pixel can't fire (no fbq, no pixel IDs, SSR).
function fireFbq(eventName: FunnelEventName, params: FunnelEventParams): string | null {
  if (!canFireFbq()) return null;

  const eventId = newEventId();
  window.fbq('track', eventName, params, { eventID: eventId });
  return eventId;
}

// Browser-only - there is no natural mutation to piggyback on for product views.
export function trackViewContent(productId: string, contentName: string, value: number, currency: string): void {
  fireFbq('ViewContent', {
    content_ids: [productId],
    content_name: contentName,
    content_type: 'product',
    value,
    currency,
  });
}

// Returns eventId; pass it to addToCart() so the server fires CAPI AddToCart
// as a side effect of POST /cart/add with the same id.
export function trackAddToCart(productId: string, contentName: string, value: number, currency: string, quantity: number): string | null {
  return fireFbq('AddToCart', {
    content_ids: [productId],
    content_name: contentName,
    content_type: 'product',
    value: value * quantity,
    currency,
    num_items: quantity,
  });
}

// Returns eventId; pass it to createOrder() so the server fires CAPI
// InitiateCheckout as a side effect of POST /gift/buy with the same id.
export function trackInitiateCheckout(value: number, currency: string, contentIds: string[], numItems: number): string | null {
  return fireFbq('InitiateCheckout', {
    content_ids: contentIds,
    content_type: 'product',
    value,
    currency,
    num_items: numItems,
  });
}

export function getFbp(): string | null {
  if (typeof window === 'undefined') return null;
  const fbpCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbp='));
  return fbpCookie ? fbpCookie.split('=')[1] : null;
}

export function getFbc(): string | null {
  if (typeof window === 'undefined') return null;
  const fbcCookie = document.cookie.split('; ').find((row) => row.startsWith('_fbc='));
  return fbcCookie ? fbcCookie.split('=')[1] : null;
}
